import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import QuizProgressTracker from './QuizProgressTracker';
import QuizResultDisplay from './QuizResultDisplay';
import MathText from '../../../../../common/MathText';
import { sheetToJsonPreserveRichText } from '../../../../../../utils/quizExcelParser';

const QUESTIONS_PER_PAGE = 5;

/**
 * COMPONENT CHÍNH: QuizVideoPlaying
 * 
 * Mục đích: Hiển thị bài trắc nghiệm từ file Excel
 * 
 * Tính năng:
 * 1. Đọc file Excel chứa câu hỏi trắc nghiệm
 * 2. Hỗ trợ 3 loại câu hỏi:
 *    - Single choice: Chọn 1 đáp án (A/B/C/D)
 *    - Multiple choice: Chọn nhiều đáp án (A,B,D)
 *    - Boolean: Đúng/Sai (2 đáp án)
 * 3. Render công thức toán học LaTeX trong câu hỏi/đáp án
 * 4. Hiển thị kết quả sau khi nộp bài
 * 5. Giải thích đáp án đúng
 * 
 * Props:
 * - excelUrl: Đường dẫn file Excel chứa câu hỏi
 */
export default React.memo(function QuizVideoPlaying({ excelUrl }) {
    // ===== STATES =====
    const [questions, setQuestions] = useState([]); // Danh sách câu hỏi từ Excel
    const [currentPage, setCurrentPage] = useState(0); // Trang câu hỏi đang hiển thị
    const [selectedAnswers, setSelectedAnswers] = useState({}); // Đáp án đã chọn: { questionId: answerIndex hoặc Set }
    const [showResults, setShowResults] = useState(false); // Có hiển thị kết quả không?
    const [score, setScore] = useState(0); // Số câu trả lời đúng
    const [loading, setLoading] = useState(true); // Đang load Excel?
    const [error, setError] = useState(null); // Lỗi khi load Excel
    const [quizStarted, setQuizStarted] = useState(false); // Đã bắt đầu làm bài chưa?
    const [durationMinutes, setDurationMinutes] = useState('35'); // Thời gian người dùng chọn (phút)
    const [timeLeft, setTimeLeft] = useState(0); // Thời gian còn lại (giây)
    const [timedOut, setTimedOut] = useState(false); // Bài bị nộp do hết giờ?
    const [timeConfigError, setTimeConfigError] = useState(''); // Lỗi nhập thời gian
    // Mốc đầu của khối quiz để cuộn về đúng vị trí sau khi chuyển trang.
    const quizContainerRef = useRef(null);
    // Cờ này giúp effect biết lần đổi currentPage nào cần cuộn lên đầu.
    const shouldScrollToTopRef = useRef(false);

    /**
     * HÀM ĐỌC FILE EXCEL
     * 
     * Luồng xử lý:
     * 1. Fetch file Excel từ URL
     * 2. Đọc ArrayBuffer
     * 3. Parse bằng XLSX library
     * 4. Chuyển sheet đầu tiên thành JSON
     * 5. Parse thành format câu hỏi chuẩn
     * 6. Lưu vào state questions
     */
    const loadExcelQuiz = useCallback(async (url) => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch file Excel
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // Đọc dữ liệu binary
            const arrayBuffer = await response.arrayBuffer();
            if (arrayBuffer.byteLength === 0) {
                throw new Error('File Excel rỗng');
            }
            
            // Parse Excel thành workbook
            const workbook = XLSX.read(arrayBuffer, { type: 'array', cellHTML: true, cellText: true });
            if (workbook.SheetNames.length === 0) {
                throw new Error('File Excel không có sheet nào');
            }
            
            // Lấy sheet đầu tiên
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // Chuyển sheet thành JSON (mỗi row = 1 object)
            const jsonData = sheetToJsonPreserveRichText(worksheet);
            
            if (jsonData.length === 0) {
                throw new Error('Sheet không có dữ liệu nào');
            }
            
            // Parse JSON thành format câu hỏi chuẩn
            const parsedQuestions = parseQuestions(jsonData);
            if (parsedQuestions.length === 0) {
                throw new Error('Không tìm thấy câu hỏi hợp lệ trong file');
            }
            
            setQuestions(parsedQuestions);
            setLoading(false);
        } catch (err) {
            console.error('Error loading Excel quiz:', err);
            setError(err.message || 'Không thể tải file câu hỏi');
            setLoading(false);
        }
    }, []); // Empty dependencies vì không phụ thuộc state/props nào

    // Load Excel khi component mount hoặc excelUrl thay đổi
    useEffect(() => {
        if (excelUrl) {
            loadExcelQuiz(excelUrl);
        }
    }, [excelUrl, loadExcelQuiz]);

    /**
     * HÀM PARSE DỮ LIỆU EXCEL THÀNH CÂU HỎI
     * 
     * Input: Array of objects từ XLSX (mỗi row = 1 object)
     * Output: Array of question objects
     * 
     * Format Excel yêu cầu:
     * | Câu hỏi | Đáp án A | Đáp án B | Đáp án C | Đáp án D | Đáp án đúng | Loại câu hỏi | Giải thích |
     * 
     * Đáp án đúng có thể là:
     * - "A" → Single choice
     * - "A,B,D" → Multiple choice
     * 
     * Loại câu hỏi:
     * - "single" → Chọn 1 đáp án
     * - "multiple" → Chọn nhiều đáp án
     * - "boolean" → Đúng/Sai (tự động detect nếu chỉ có 2 đáp án)
     */
    const parseQuestions = (data) => {
        return data
            .map((row, index) => {
                // ✅ Lấy câu hỏi (hỗ trợ nhiều tên cột)
                const question = String(row['Câu hỏi'] || row['Question'] || row['CauHoi'] || '').trim();
                
                // Bỏ qua dòng không có câu hỏi
                if (!question) {
                    console.warn(`Dòng ${index + 2} bị bỏ qua: Không có câu hỏi`);
                    return null;
                }

                // ✅ Lấy loại câu hỏi
                let questionType = String(row['Loại câu hỏi'] || row['Type'] || row['LoaiCauHoi'] || '').toLowerCase().trim();

                // ✅ NẾU LÀ TEXT INPUT
                if (questionType === 'text') {
                    // Lấy đáp án mẫu (có thể nhiều đáp án cách nhau bằng dấu ;)
                    const sampleAnswers = String(
                        row['Đáp án mẫu'] || 
                        row['Sample Answer'] || 
                        row['DapAnMau'] || 
                        ''
                    ).trim();

                    if (!sampleAnswers) {
                        console.warn(`Dòng ${index + 2}: Câu hỏi text phải có "Đáp án mẫu"`);
                        return null;
                    }

                    // Parse các đáp án mẫu: "x=2;x=3" hoặc "3.14" hoặc "Python;python"
                    const acceptableAnswers = sampleAnswers
                        .split(';')
                        .map(ans => ans.trim().toLowerCase()) // Lowercase để so sánh không phân biệt hoa thường
                        .filter(ans => ans !== '');

                    return {
                        id: index + 1,
                        question: question,
                        questionType: 'text',
                        options: [], // Text input không có options
                        correctAnswers: acceptableAnswers, // Mảng các đáp án chấp nhận được
                        explanation: String(
                            row['Giải thích'] || 
                            row['Explanation'] || 
                            row['GiaiThich'] || 
                            row['explanation'] || 
                            ''
                        ).trim(),
                        // ✅ Thêm config cho text input
                        textInputConfig: {
                            caseSensitive: row['Phân biệt hoa thường'] === 'TRUE' || false, // Có phân biệt hoa/thường?
                            exactMatch: row['Khớp chính xác'] === 'TRUE' || false, // Khớp chính xác hay chứa chuỗi con?
                            placeholder: String(row['Placeholder'] || 'Nhập câu trả lời...').trim()
                        }
                    };
                }

                // ✅ XỬ LÝ SINGLE/MULTIPLE/BOOLEAN (code cũ)
                const options = [
                    row['Đáp án A'] || row['A'] || row['DapAnA'] || row['a'] || '',
                    row['Đáp án B'] || row['B'] || row['DapAnB'] || row['b'] || '',
                    row['Đáp án C'] || row['C'] || row['DapAnC'] || row['c'] || '',
                    row['Đáp án D'] || row['D'] || row['DapAnD'] || row['d'] || ''
                ].filter(opt => {
                    // Lọc bỏ đáp án rỗng
                    if (!opt) return false;
                    const str = String(opt).trim();
                    return str !== '';
                });

                // Ít nhất phải có 2 đáp án
                if (options.length < 2) {
                    console.warn(`Dòng ${index + 2} bị bỏ qua: Ít hơn 2 đáp án`);
                    return null;
                }

                // ✅ Lấy đáp án đúng (có thể là "A" hoặc "A,B,D")
                const correctAnswerRaw = String(
                    row['Đáp án đúng'] || 
                    row['Correct'] || 
                    row['DapAnDung'] || 
                    row['correct'] || 
                    'A' // Mặc định là A nếu không có
                ).toUpperCase().trim();

                // Parse thành array: "A,B,D" → ["A", "B", "D"]
                const correctAnswers = correctAnswerRaw
                    .split(',')
                    .map(ans => ans.trim())
                    .filter(ans => ['A', 'B', 'C', 'D'].includes(ans));

                // Nếu không có đáp án hợp lệ, mặc định là A
                if (correctAnswers.length === 0) {
                    console.warn(`Dòng ${index + 2}: Đáp án đúng không hợp lệ "${correctAnswerRaw}", mặc định là "A"`);
                    correctAnswers.push('A');
                }

                // ✅ Xác định loại câu hỏi
                // let questionType = String(row['Loại câu hỏi'] || row['Type'] || row['LoaiCauHoi'] || '').toLowerCase().trim();
                
                // Tự động detect nếu không có loại
                if (!questionType) {
                    if (options.length === 2) {
                        questionType = 'boolean'; // Đúng/Sai
                    } else if (correctAnswers.length > 1) {
                        questionType = 'multiple'; // Nhiều đáp án đúng
                    } else {
                        questionType = 'single'; // Chỉ 1 đáp án đúng
                    }
                }

                // ✅ Return object câu hỏi chuẩn
                return {
                    id: index + 1, // ID duy nhất
                    question: question, // Nội dung câu hỏi (có thể chứa LaTeX)
                    options, // Mảng các đáp án ["Đáp án A", "Đáp án B", ...]
                    correctAnswers, // Mảng đáp án đúng ["A", "B", "D"]
                    questionType, // Loại: single/multiple/boolean
                    explanation: String(
                        row['Giải thích'] || 
                        row['Explanation'] || 
                        row['GiaiThich'] || 
                        row['explanation'] || 
                        ''
                    ).trim() // Giải thích (có thể chứa LaTeX)
                };
            })
            .filter(q => q !== null); // Bỏ qua các dòng null (không hợp lệ)
    };

    /**
     * HÀM XỬ LÝ CHỌN ĐÁP ÁN
     * 
     * Logic:
     * - Single/Boolean: Chọn 1 đáp án → thay thế đáp án cũ
     * - Multiple: Chọn nhiều → toggle (thêm/bỏ) đáp án vào Set
     * 
     * State selectedAnswers:
     * {
     *   1: 0,              // Câu 1: chọn đáp án A (index 0)
     *   2: Set([0, 2, 3]), // Câu 2: chọn A, C, D (multiple)
     *   3: 1               // Câu 3: chọn đáp án B (index 1)
     * }
     */
    const handleAnswerSelect = (questionId, answerIndex) => {
        const question = questions.find(q => q.id === questionId);
        if (!question) return;

        setSelectedAnswers(prev => {
            const newAnswers = { ...prev };

            // ✅ NẾU LÀ TEXT INPUT
            if (question.questionType === 'text') {
                // answerIndex ở đây chính là giá trị text user nhập
                newAnswers[questionId] = answerIndex; // Lưu trực tiếp string
                return newAnswers;
            }

            // Multiple choice (code cũ)
            if (question.questionType === 'multiple') {
                // Multiple choice: dùng Set để lưu nhiều đáp án
                const currentSet = newAnswers[questionId] || new Set();
                const newSet = new Set(currentSet);
                
                // Toggle: nếu đã chọn thì bỏ, chưa chọn thì thêm
                if (newSet.has(answerIndex)) {
                    newSet.delete(answerIndex);
                } else {
                    newSet.add(answerIndex);
                }
                
                newAnswers[questionId] = newSet;
            } else {
                // Single/Boolean choice: lưu trực tiếp index
                newAnswers[questionId] = answerIndex;
            }

            return newAnswers;
        });
    };

    /**
     * HÀM KIỂM TRA ĐÁP ÁN ĐÚNG
     * 
     * Logic:
     * - Multiple: So sánh Set đáp án đã chọn với Set đáp án đúng
     * - Single/Boolean: So sánh letter (A/B/C/D)
     * 
     * Return: true nếu đúng, false nếu sai
     */
    const isAnswerCorrect = (question, selectedAnswer) => {
        // ✅ KIỂM TRA TEXT INPUT
        if (question.questionType === 'text') {
            if (!selectedAnswer || typeof selectedAnswer !== 'string') return false;

            const userAnswer = selectedAnswer.trim();
            
            // Nếu user không nhập gì
            if (userAnswer === '') return false;

            const config = question.textInputConfig || {};

            // Normalize: chuyển về lowercase nếu không phân biệt hoa/thường
            const normalizedUserAnswer = config.caseSensitive 
                ? userAnswer 
                : userAnswer.toLowerCase();

            // ✅ KIỂM TRA XEM USER ANSWER CÓ KHỚP VỚI BẤT KỲ CORRECT ANSWER NÀO KHÔNG
            return question.correctAnswers.some(correctAns => {
                const normalizedCorrectAns = config.caseSensitive 
                    ? correctAns 
                    : correctAns.toLowerCase();

                // ✅ KHỚP CHÍNH XÁC
                if (config.exactMatch) {
                    return normalizedUserAnswer === normalizedCorrectAns;
                }

                // ✅ CHỨA CHUỖI CON (CHỈ 1 CHIỀU: USER ANSWER CHỨA CORRECT ANSWER)
                // Ví dụ:
                // - Correct: "x=2"
                // - User nhập: "x=2 hoặc x=3" → includes("x=2") = true → Đúng ✓
                // - User nhập: "x" → includes("x=2") = false → Sai ✓
                // - User nhập: "x=2" → includes("x=2") = true → Đúng ✓
                return normalizedUserAnswer.includes(normalizedCorrectAns);
            });
        }

        // ✅ KIỂM TRA MULTIPLE CHOICE
        if (question.questionType === 'multiple') {
            if (!(selectedAnswer instanceof Set)) return false;
            
            const selectedLetters = Array.from(selectedAnswer)
                .map(idx => String.fromCharCode(65 + idx))
                .sort();
            
            const correctLetters = [...question.correctAnswers].sort();
            
            return JSON.stringify(selectedLetters) === JSON.stringify(correctLetters);
        }

        // ✅ KIỂM TRA SINGLE/BOOLEAN
        if (selectedAnswer === undefined) return false;
        
        const selectedLetter = String.fromCharCode(65 + selectedAnswer);
        return question.correctAnswers.includes(selectedLetter);
    };

    const finishQuiz = useCallback((isTimedOut = false) => {
        const correctCount = questions.reduce((count, q) => {
            const selectedAnswer = selectedAnswers[q.id];
            return count + (isAnswerCorrect(q, selectedAnswer) ? 1 : 0);
        }, 0);

        setScore(correctCount);
        setTimedOut(isTimedOut);
        setShowResults(true);
    }, [questions, selectedAnswers]);

    // Dùng window.scrollTo thay vì scrollIntoView vì header đang sticky.
    const scrollToQuizTop = useCallback(() => {
        if (!quizContainerRef.current) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Trừ nhẹ một khoảng để khối quiz không dính sát mép trên viewport.
        const topOffset = 12;
        const absoluteTop = quizContainerRef.current.getBoundingClientRect().top + window.scrollY - topOffset;

        window.scrollTo({
            top: Math.max(absoluteTop, 0),
            behavior: 'smooth'
        });
    }, []);

    /**
     * CÁC HÀM ĐIỀU HƯỚNG
     */
    const handleNextQuestion = () => {
        if (currentPage < totalPages - 1) {
            // Đánh dấu để effect cuộn sau khi React render xong trang câu hỏi mới.
            shouldScrollToTopRef.current = true;
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentPage > 0) {
            // Giữ hành vi cuộn đồng bộ với nút sang trang tiếp theo.
            shouldScrollToTopRef.current = true;
            setCurrentPage(prev => prev - 1);
        }
    };

    /**
     * HÀM NỘP BÀI
     * 
     * Tính điểm: Đếm số câu trả lời đúng
     */
    const handleSubmitQuiz = () => {
        finishQuiz(false);
    };

    const handleStartQuiz = () => {
        const parsedMinutes = Number(durationMinutes);

        if (!Number.isFinite(parsedMinutes) || parsedMinutes <= 0) {
            setTimeConfigError('Vui lòng nhập thời gian lớn hơn 0 phút.');
            return;
        }

        setTimeConfigError('');
        setTimedOut(false);
        setTimeLeft(Math.floor(parsedMinutes * 60));
        setQuizStarted(true);
    };

    /**
     * HÀM LÀM LẠI BÀI THI
     * 
     * Reset tất cả state về ban đầu
     */
    const handleRetakeQuiz = () => {
        setSelectedAnswers({});
        setCurrentPage(0);
        setShowResults(false);
        setScore(0);
        setTimedOut(false);
        setQuizStarted(false);
        setTimeLeft(0);
        setTimeConfigError('');
    };

    /**
     * COMPUTED VALUES (useMemo để tối ưu)
     */

    const totalPages = useMemo(() => {
        if (questions.length === 0) return 0;
        return Math.ceil(questions.length / QUESTIONS_PER_PAGE);
    }, [questions.length]);

    const pageStartIndex = useMemo(() => {
        return currentPage * QUESTIONS_PER_PAGE;
    }, [currentPage]);

    const currentPageQuestions = useMemo(() => {
        return questions.slice(pageStartIndex, pageStartIndex + QUESTIONS_PER_PAGE);
    }, [questions, pageStartIndex]);

    const currentRangeEnd = useMemo(() => {
        return Math.min(pageStartIndex + currentPageQuestions.length, questions.length);
    }, [pageStartIndex, currentPageQuestions.length, questions.length]);

    // Tiến độ hoàn thành (%)
    const progress = useMemo(() => {
        if (questions.length === 0) return 0;
        return (currentRangeEnd / questions.length) * 100;
    }, [currentRangeEnd, questions.length]);

    const hasAnswerForQuestion = useCallback((question) => {
        if (question.questionType === 'multiple') {
            const selectedSet = selectedAnswers[question.id];
            return selectedSet instanceof Set && selectedSet.size > 0;
        }

        if (question.questionType === 'text') {
            const selectedText = selectedAnswers[question.id];
            return typeof selectedText === 'string' && selectedText.trim() !== '';
        }

        return selectedAnswers[question.id] !== undefined;
    }, [selectedAnswers]);

    const handleJumpToQuestion = useCallback((questionIndex) => {
        // Nhảy nhanh sang trang chứa câu được chọn rồi cuộn về đầu khối quiz.
        shouldScrollToTopRef.current = true;
        setCurrentPage(Math.floor(questionIndex / QUESTIONS_PER_PAGE));
    }, []);

    useEffect(() => {
        if (!shouldScrollToTopRef.current) {
            return;
        }

        // Chỉ cuộn sau khi currentPage đã cập nhật xong để tránh cuộn sai vị trí.
        shouldScrollToTopRef.current = false;
        scrollToQuizTop();
    }, [currentPage, scrollToQuizTop]);

    useEffect(() => {
        if (!quizStarted || showResults || timeLeft <= 0) {
            return undefined;
        }

        const timerId = setInterval(() => {
            setTimeLeft(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(timerId);
    }, [quizStarted, showResults, timeLeft]);

    useEffect(() => {
        if (quizStarted && !showResults && timeLeft === 0) {
            finishQuiz(true);
        }
    }, [quizStarted, showResults, timeLeft, finishQuiz]);

    const isLastMinute = timeLeft > 0 && timeLeft <= 60;

    /**
     * RENDER LOADING STATE
     */
    if (loading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Đang tải...</span>
                </div>
                <p className="mt-2">Đang tải câu hỏi...</p>
            </div>
        );
    }

    /**
     * RENDER ERROR STATE
     */
    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Lỗi:</strong> {error}
                <div className="mt-2">
                    <small>
                        <strong>Gợi ý:</strong>
                        <ul className="mb-0 mt-1">
                            <li>Kiểm tra file Excel có đúng format không</li>
                            <li>Sheet phải có cột: Câu hỏi, Đáp án A, B, C, D, Đáp án đúng</li>
                            <li>Đáp án đúng có thể là: A hoặc A,B,D (nhiều đáp án)</li>
                        </ul>
                    </small>
                </div>
            </div>
        );
    }

    /**
     * RENDER EMPTY STATE
     */
    if (questions.length === 0 || currentPageQuestions.length === 0) {
        return (
            <div className="alert alert-warning" role="alert">
                <i className="fas fa-info-circle me-2"></i>
                Không tìm thấy câu hỏi hợp lệ trong file Excel
            </div>
        );
    }

    /**
     * ===== RENDER MAIN UI =====
     */
    return (
        <div ref={quizContainerRef} className="card border-0">
            <div className="card-body body-quiz">
                {!showResults ? (
                    /* ===== MÀN HÌNH LÀM BÀI ===== */
                    <>
                        

                        <QuizProgressTracker
                            quizStarted={quizStarted}
                            isLastMinute={isLastMinute}
                            timeLeft={timeLeft}
                            durationMinutes={durationMinutes}
                            onDurationChange={setDurationMinutes}
                            onStartQuiz={handleStartQuiz}
                            timeConfigError={timeConfigError}
                            questions={questions}
                            hasAnswerForQuestion={hasAnswerForQuestion}
                            pageStartIndex={pageStartIndex}
                            currentRangeEnd={currentRangeEnd}
                            onJumpToQuestion={handleJumpToQuestion}
                            onRetakeQuiz={handleRetakeQuiz}
                            onSubmitQuiz={handleSubmitQuiz}
                            progress={progress}
                        />

                        {/* QUESTION: Hiển thị nhóm câu hỏi */}
                        <div className="d-flex flex-column gap-2 mb-4" >
                            {currentPageQuestions.map((question) => (
                                <div key={question.id} className="p-2 bg-light rounded">
                                    <div className="d-flex align-items-start mb-1 gap-1 flex-wrap quiz-question-heading">
                                        <span className="q-label flex-shrink-0">Câu {question.id}.</span>
                                        <div className="mb-0 quiz-question-text flex-grow-1" style={{ minWidth: 0 }}>
                                            <MathText text={question.question} />
                                        </div>

                                        <div className="d-flex gap-1 ms-2 flex-shrink-0">
                                            {question.questionType === 'text' && (
                                                <span className="badge bg-secondary">
                                                    <i className="fas fa-keyboard me-1"></i>
                                                    Trả lời ngắn
                                                </span>
                                            )}
                                            {question.questionType === 'multiple' && (
                                                <span className="badge bg-warning text-dark">
                                                    <i className="fas fa-check-double me-1"></i>
                                                    Chọn nhiều đáp án
                                                </span>
                                            )}
                                            {question.questionType === 'boolean' && (
                                                <span className="badge bg-info">
                                                    <i className="fas fa-question-circle me-1"></i>
                                                    Đúng/Sai
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {question.questionType === 'text' ? (
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                className="form-control form-control-lg quiz-text-input"
                                                placeholder={question.textInputConfig?.placeholder || 'Nhập câu trả lời...'}
                                                value={selectedAnswers[question.id] || ''}
                                                onChange={(e) => handleAnswerSelect(question.id, e.target.value)}
                                            />
                                            <small className="text-muted d-block mt-2">
                                                <i className="fas fa-info-circle me-1"></i>
                                                {question.textInputConfig?.caseSensitive
                                                    ? 'Phân biệt chữ hoa/thường'
                                                    : 'Không phân biệt chữ hoa/thường'}
                                                {' • '}
                                                {question.textInputConfig?.exactMatch
                                                    ? 'Khớp chính xác'
                                                    : 'Chứa từ khóa'}
                                            </small>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-1">
                                            {question.options.map((option, index) => {
                                                const letter = String.fromCharCode(65 + index);

                                                let isSelected = false;
                                                if (question.questionType === 'multiple') {
                                                    const selectedSet = selectedAnswers[question.id];
                                                    isSelected = selectedSet instanceof Set && selectedSet.has(index);
                                                } else {
                                                    isSelected = selectedAnswers[question.id] === index;
                                                }

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`quiz-option-card d-flex align-items-start rounded ${
                                                            isSelected
                                                                ? 'bg-primary opt'
                                                                : 'bg-answers '
                                                        }`}
                                                        onClick={() => handleAnswerSelect(question.id, index)}
                                                    >
                                                        <div className="d-flex align-items-center w-100 quiz-option-content">
                                                            <div
                                                                className={`me-3 d-flex align-items-center justify-content-center ${
                                                                    question.questionType === 'multiple'
                                                                        ? 'rounded'
                                                                        : 'rounded-circle'
                                                                } ${
                                                                    isSelected ? 'bg-white text-primary' : 'bg-light'
                                                                } quiz-option-badge ${
                                                                    question.questionType === 'multiple' && isSelected
                                                                        ? 'quiz-option-badge-multiple-selected'
                                                                        : ''
                                                                }`}
                                                            >
                                                                {question.questionType === 'multiple' && isSelected ? (
                                                                    <i className="fas fa-check"></i>
                                                                ) : (
                                                                    letter
                                                                )}
                                                            </div>
                                                            <span className="quiz-option-text">
                                                                <MathText text={option} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {question.questionType === 'multiple' && (
                                        <div className="alert alert-warning mt-3 mb-0" role="alert">
                                            <small>
                                                <i className="fas fa-info-circle me-1"></i>
                                                <strong>Lưu ý:</strong> Câu hỏi này có nhiều đáp án đúng. Chọn TẤT CẢ đáp án đúng để được điểm.
                                            </small>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* NAVIGATION: Nút điều hướng */}
                        <div className="d-flex justify-content-between mb-4">
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={handlePreviousQuestion}
                                disabled={currentPage === 0}
                            >
                                <i className="fas fa-arrow-left me-2"></i>
                                5 câu trước
                            </button>

                            {/* Nút "Nộp bài" ở trang cuối, "5 câu tiếp" ở các trang khác */}
                            {currentPage === totalPages - 1 ? (
                                <button 
                                    className="btn btn-success"
                                    onClick={handleSubmitQuiz}
                                >
                                    <i className="fas fa-check me-2"></i>
                                    Nộp bài
                                </button>
                            ) : (
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleNextQuestion}
                                >
                                    5 câu tiếp
                                    <i className="fas fa-arrow-right ms-2"></i>
                                </button>
                            )}
                        </div>

                        
                    </>
                ) : (
                    /* ===== MÀN HÌNH KẾT QUẢ ===== */
                    <QuizResultDisplay
                        score={score}
                        questions={questions}
                        timedOut={timedOut}
                        selectedAnswers={selectedAnswers}
                        isAnswerCorrect={isAnswerCorrect}
                        onRetakeQuiz={handleRetakeQuiz}
                        MathTextComponent={MathText}
                    />
                )}
            </div>
        </div>
    );
});